// BASIC
import { useState } from 'react';
import PropTypes from 'prop-types';
// Libs
// Store & actions
import actionsSims from '../../api/actionsSims';
// Components
import HeaderTitle from '../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import SimCardsTable from '../tables/SimCardsTable';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import simCardFields from '../../fields/simCardFields';
import SIM_CARDS_STATUS from '../../enums/SimCardStatus';
import SIM_OFFERS_PROVIDERS from '../../enums/SimOffersProviders';
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @param root0.simCard
 * @returns {JSX} ModalReplaceSimCard
 */
function ModalReplaceSimCard({ isOpen, setIsOpen, setLaunchRefetch, simCard }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.replace.steps';

    // States
    const [step, setStep] = useState(0);
    const [simCardsSelected, setSimCardsSelected] = useState(getInitialValueForRowSelected());

    // Api calls
    const onSuccess = () => {
        setLaunchRefetch(true);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.replace.success' namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.replace.error' namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitReplaceSimCard = actionsSims.replaceSimCard(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.sim_selection`, disabledNext: simCardsSelected.rowsData.length === 0 },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [simCardsSelected]
    );

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step !== 0 ? 'max-w-lg' : 'max-w-6xl'}>
                <HeaderTitle title='modal.replace.title' namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <SimCardsTable
                            filtersEventName={setFiltersEventName('sim_cards', true)}
                            customQuery={{
                                filters: [
                                    {
                                        key: generateNestedFieldAccessor(simCardFields.SIM_CARD.STATUS),
                                        value: SIM_CARDS_STATUS.INACTIVE.name,
                                        hidden: true
                                    },
                                    {
                                        key: generateNestedFieldAccessor(simCardFields.SIM_CARD.PROVIDER),
                                        value: SIM_OFFERS_PROVIDERS.BOUYGUES_TELECOM.name,
                                        hidden: true
                                    }
                                ]
                            }}
                            rowsSelected={simCardsSelected}
                            setRowsSelected={setSimCardsSelected}
                            singleSelection={true}
                            isFromModal={true}
                        />
                    }
                    {
                        step === 1 &&
                        <div className='text-center space-y-3'>
                            <div>
                                <Entity entity='modal.replace.replacement' namespace={nsSimCard} className='font-normal'
                                    data={{ previousSim: simCard.sim.iccid, nextSim: simCardsSelected.rowsData[0].sim.iccid }}
                                />
                            </div>
                            <div>
                                <Entity entity='modal.replace.text_1' namespace={nsSimCard} className='font-normal' />
                            </div>
                            <div>
                                <Entity entity='modal.replace.text_2' namespace={nsSimCard} className='font-normal' />
                            </div>
                        </div>
                    }

                </ModalBody>
                <FooterStep
                    step={step}
                    stepsNumber={STEPS_NUMBER}
                    setStep={setStep}
                    onValidate={() => {
                        submitReplaceSimCard.mutate({
                            simUuid: simCard.sim.uuid,
                            iccid: simCardsSelected.rowsData[0].sim.iccid
                        });
                    }}
                    isLoading={submitReplaceSimCard.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal >
    );
}

ModalReplaceSimCard.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func,
    simCard: PropTypes.object
};
export default ModalReplaceSimCard;